import type { Funder, InvoicingRateCard } from '../types/invoicingRates'
import { resolveEffectiveRate, resolveFundingChain } from '../utils'
import { useGetFunders } from './useFunders'
import { useGetInvoicingRateCards } from './useInvoicingRates'
import { useGetResidentFunding } from './useResidentFunding'

export interface EventInvoicingRateOverride {
  rateCardId?: string
  customRate?: number
  unit?: string
}

interface UseVisitRateCardResult {
  funder: Funder | null
  rateCard: InvoicingRateCard | null
  rateCardLabel: string | null
  isLoading: boolean
}

/**
 * Resolves the effective invoicing rate for a visit.
 *
 * Priority (most specific first):
 *   1. Event-level override (invoicingRateOverride stored on the revision)
 *   2. Contract rate card resolved via funding chain (with visit-type overrides applied)
 *
 * @param residentId    - the visit's subjectId
 * @param fundingTypeId - the visit's fundingTypeId (new Funder._id or legacy externalFundingTypeId)
 * @param atDate        - ISO datetime of the visit (for date-accurate rate card resolution)
 * @param eventTypeId   - the visit's instanceTypeId (for visit-type rate overrides on the card)
 * @param eventOverride - the revision's invoicingRateOverride field, if any
 */
export const useVisitRateCard = (
  residentId: string | null | undefined,
  fundingTypeId: string | null | undefined,
  atDate?: string | null,
  eventTypeId?: string | null,
  eventOverride?: EventInvoicingRateOverride | null
): UseVisitRateCardResult => {
  const { data: funders = [], isLoading: loadingFunders } = useGetFunders()
  const { data: residentFundings = [], isLoading: loadingFundings } =
    useGetResidentFunding(residentId ?? '')
  const { data: rateCards = [], isLoading: loadingRateCards } =
    useGetInvoicingRateCards()

  const isLoading = loadingFunders || loadingFundings || loadingRateCards

  if (isLoading) {
    return { funder: null, rateCard: null, rateCardLabel: null, isLoading }
  }

  // 1. Event-level override takes highest priority
  if (eventOverride) {
    // Resolve funder for the label even when overriding the rate
    const funder =
      funders.find((f) => f._id === fundingTypeId) ??
      funders.find((f) => f.externalFundingTypeId === fundingTypeId) ??
      null

    if (eventOverride.rateCardId) {
      const rateCard =
        rateCards.find((c) => c._id === eventOverride.rateCardId) ?? null
      const rateCardLabel = rateCard
        ? `${rateCard.currency}${parseFloat(resolveEffectiveRate(rateCard, eventTypeId).rate)} / ${rateCard.unit.toLowerCase()} (override)`
        : null
      return { funder, rateCard, rateCardLabel, isLoading }
    }

    if (eventOverride.customRate != null) {
      const unit = eventOverride.unit ?? 'hour'
      const rateCardLabel = `GBP${eventOverride.customRate} / ${unit.toLowerCase()} (custom)`
      return { funder, rateCard: null, rateCardLabel, isLoading }
    }
  }

  // 2. Fall back to contract rate resolved through the funding chain
  if (!fundingTypeId) {
    return { funder: null, rateCard: null, rateCardLabel: null, isLoading }
  }

  const result = resolveFundingChain(
    fundingTypeId,
    funders,
    residentFundings,
    rateCards,
    atDate,
    eventTypeId
  )

  return { ...result, isLoading }
}
